import {
  ApartmentTwoTone,
  BedTwoTone,
  CalendarMonthTwoTone,
  DoorFrontTwoTone,
  InfoTwoTone,
  LanguageTwoTone,
  LocationOnTwoTone,
  NightsStayTwoTone,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HotelPresentationInfoDialog } from '../pages/HotelPresentationInfo';
import React, { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';

export function HotelCard({
  accommodationStayId,
  imageUrl,
  stars,
  name,
  stayNight,
  stayDays,
  startDate,
  endDate,
  webSite,
  latitude,
  longitude,
  roomType,
  bedType,
  roomNumber,
  city,
}: {
  accommodationStayId?: number;
  imageUrl?: string;
  stars?: number;
  name?: string;
  stayNight: number;
  stayDays: number;
  startDate: string;
  endDate: string;
  webSite?: string;
  latitude?: string;
  longitude?: string;
  roomType?: string;
  bedType?: string;
  roomNumber?: number;
  city?: string;
}) {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const showSnackbar = useAlertSnackbar();
  const [locationURL, setLocationURL] = useState<string>('');

  const handleShare = (
    event: React.MouseEvent<HTMLElement>,
    latitude?: string,
    longitude?: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setLocationURL(
      `https://www.google.com/maps/place/${latitude}+${longitude}`,
    );
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(locationURL);
    showSnackbar(t('guideCustomerOperationState.copied'), 'success');
    handleCloseMenu();
  };

  const handleShareWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      locationURL,
    )}`;
    window.open(whatsappUrl, '_blank');
    handleCloseMenu();
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Card>
      {imageUrl && (
        <CardMedia sx={{ height: 125 }}>
          <img
            src={imageUrl}
            alt="Hotel"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </CardMedia>
      )}
      {!imageUrl && name && (
        <CardMedia
          sx={{
            height: 125,
            textAlign: 'center',
            width: '100%',
          }}
        >
          <ApartmentTwoTone sx={{ fontSize: '100px' }} />
        </CardMedia>
      )}
      <CardContent sx={{ pb: 0 }}>
        {!imageUrl && !name && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              'accommodationCityCardPart.theHotelWhereYouWillStayIsBeingPlanned',
            )}
          </Alert>
        )}
        {name && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {' '}
              <Typography variant="h6">{name}</Typography>
              <Typography>
                {t('accommodationCityCardPart.ectHotels')}
              </Typography>
            </Box>

            {stars && <HotelRating value={stars} />}
          </Box>
        )}
        <List sx={{ p: 0, mx: -2 }}>
          {city && (
            <ListItem>
              <ListItemIcon>
                <LocationOnTwoTone />
              </ListItemIcon>
              <ListItemText primary={city} />
            </ListItem>
          )}
          <ListItem>
            <ListItemIcon>
              <NightsStayTwoTone />
            </ListItemIcon>
            <ListItemText
              primary={t('accommodationCityCardPart.stayDays', {
                days: stayDays,
                nights: stayNight,
              })}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CalendarMonthTwoTone />
            </ListItemIcon>
            <ListItemText
              primary={
                <span>
                  {new Date(startDate).toLocaleString('tr-TR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    weekday: 'long',
                  })}
                  <br />
                  {new Date(endDate).toLocaleString('tr-TR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    weekday: 'long',
                  })}
                </span>
              }
            />
          </ListItem>
          {(roomType || bedType) && (
            <ListItem>
              <ListItemIcon>
                <BedTwoTone />
              </ListItemIcon>
              <ListItemText
                primary={[roomType, bedType].filter(Boolean).join(' - ')}
              />
            </ListItem>
          )}
          {roomNumber && (
            <ListItem>
              <ListItemIcon>
                <DoorFrontTwoTone />
              </ListItemIcon>
              <ListItemText
                primary={t('hotelCard.roomNumber', { roomNumber })}
              />
            </ListItem>
          )}
        </List>
      </CardContent>
      <CardActions>
        {accommodationStayId && (
          <Button onClick={handleOpenDialog} color="inherit" size="small">
            <InfoTwoTone sx={{ mr: 1 }} />
            {t('accommodationCityCardPart.learnMore')}
          </Button>
        )}
        {latitude && longitude && (
          <Button
            color="inherit"
            onClick={(e) => handleShare(e, latitude, longitude)}
            sx={{ mr: 1 }}
          >
            <LocationOnTwoTone />
            {t('accommodationCityCardPart.hotelLocation')}
          </Button>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem>
            <Button
              color="inherit"
              target="_blank"
              href={`https://www.google.com/maps/place/${latitude}+${longitude}`}
              rel="noreferrer"
            >
              <LocationOnTwoTone sx={{ mr: 1 }} />
              {t('accommodationCityCardPart.openInMap')}
            </Button>
          </MenuItem>
          <MenuItem>
            <Button color="inherit" onClick={handleCopyLink}>
              <ContentCopyIcon sx={{ mr: 1 }} />
              {t('accommodationCityCardPart.copyLocationLink')}
            </Button>
          </MenuItem>
          <MenuItem>
            <Button color="inherit" onClick={handleShareWhatsApp}>
              <WhatsAppIcon sx={{ mr: 1 }} />
              {t('accommodationCityCardPart.openInWhatsApp')}
            </Button>
          </MenuItem>{' '}
        </Menu>
        {webSite && (
          <Button
            href={webSite ? webSite : 'sas'}
            target="_blank"
            color="inherit"
          >
            <LanguageTwoTone sx={{ mr: 1 }} />
            {t('accommodationCityCardPart.hotelWeb')}
          </Button>
        )}
      </CardActions>
      <HotelPresentationInfoDialog
        stayId={accommodationStayId ? accommodationStayId : 0}
        open={openDialog}
        onClose={handleCloseDialog}
      />
    </Card>
  );
}

function HotelRating(props: { value: number }) {
  return (
    <Typography sx={{ color: '#FFD400' }}>
      {Array.from({ length: props.value }).fill('★').join('')}
    </Typography>
  );
}
